import React from 'react';
import { useRBAC, ReadOnlyProvider, PAGE_ACCESS } from './RBACContext';
import { ShieldAlert } from 'lucide-react';
import Sidebar from '../../presentation/components/ui/Sidebar';

interface ProtectedPageProps {
    children: React.ReactNode;
    page: string;
    fallback?: React.ReactNode;
}

/**
 * ProtectedPage wrapper. Used at the route level (see App.tsx → Guarded()).
 *
 * Behaviour:
 *  - Super admin always gets through with full edit access.
 *  - When the user has no entry in PAGE_ACCESS[page] for the active org,
 *    the AccessDenied screen replaces the page body but keeps the Sidebar
 *    visible so the user can still navigate to pages they CAN access.
 *  - When the user has read-only access (entry in `readOnlyRoles`), the
 *    page renders but children receive `useReadOnly() === true`, which
 *    individual pages can use to disable mutating UI.
 */
const ProtectedPage: React.FC<ProtectedPageProps> = ({
    children,
    page,
    fallback
}) => {
    const { canAccess, canEdit, isSuperAdmin } = useRBAC();

    if (isSuperAdmin) {
        return <ReadOnlyProvider isReadOnly={false}>{children}</ReadOnlyProvider>;
    }

    if (!canAccess(page)) {
        return <>{fallback || <AccessDenied page={page} />}</>;
    }

    const isReadOnly = !canEdit(page);
    return (
        <ReadOnlyProvider isReadOnly={isReadOnly}>
            {children}
        </ReadOnlyProvider>
    );
};

/**
 * Default access-denied screen. Renders alongside the global Sidebar so the
 * user still has navigation — their accessible pages remain reachable from
 * the side nav. The denied card occupies the main content area only, not
 * the whole viewport.
 */
const AccessDenied: React.FC<{ page: string }> = ({ page }) => {
    const pageAccess = PAGE_ACCESS[page];
    const requiredRoles = pageAccess?.requiredRoles || [];

    return (
        <div className="flex min-h-screen bg-gray-50">
            <Sidebar />
            <div className="flex-1 ml-16 flex items-center justify-center p-8">
                <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md">
                    <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                        <ShieldAlert className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        Access Denied
                    </h2>
                    <p className="text-gray-600 mb-4">
                        You don't have permission to access this page.
                    </p>
                    {requiredRoles.length > 0 && (
                        <div className="text-sm text-gray-500">
                            <p className="mb-2">Required roles:</p>
                            <div className="flex flex-wrap gap-2 justify-center">
                                {requiredRoles.map((role) => (
                                    <span
                                        key={role}
                                        className="px-2 py-1 bg-gray-100 rounded text-xs font-medium"
                                    >
                                        {role}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    <button
                        onClick={() => window.history.back()}
                        className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProtectedPage;
